import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from "./authService";

// API functions for bookings
export const bookingApi = {
  // Get all bookings for current user
  getBookings: async (params = {}) => {
    const response = await api.get('/bookings', { params });
    return response.data;
  },

  // Get booking by ID
  getById: async (bookingId) => {
    const response = await api.get(`/bookings/${bookingId}`);
    return response.data;
  },

  // Create new booking
  create: async (bookingData) => {
    const response = await api.post('/bookings', bookingData);
    return response.data;
  },

  // Update booking
  update: async ({ id, data }) => {
    const response = await api.put(`/bookings/${id}`, data);
    return response.data;
  },

  // Cancel booking
  cancel: async (bookingId, cancellationReason) => {
    const response = await api.put(`/bookings/${bookingId}/status`, {
      status: 'cancelled',
      cancellation_reason: cancellationReason
    });
    return response.data;
  },

  // Update booking status
  updateStatus: async ({ bookingId, status, reason }) => {
    const response = await api.put(`/bookings/${bookingId}/status`, {
      status,
      cancellation_reason: reason
    });
    return response.data;
  },

  // Get booking timeline
  getTimeline: async (bookingId) => {
    const response = await api.get(`/bookings/${bookingId}/timeline`);
    return response.data;
  },

  // Add booking notes
  addNotes: async ({ bookingId, notes }) => {
    const response = await api.post(`/bookings/${bookingId}/notes`, { notes });
    return response.data;
  },

  // Get booking reviews
  getReviews: async (bookingId) => {
    const response = await api.get(`/bookings/${bookingId}/reviews`);
    return response.data;
  },

  // Submit booking review
  submitReview: async ({ bookingId, rating, comment, title }) => {
    const response = await api.post(`/reviews`, {
      booking_id: bookingId,
      rating,
      comment,
      title
    });
    return response.data;
  }
};

// React Query hooks

// Get all bookings for current user
export const useBookings = (params = {}, options = {}) => {
  return useQuery({
    queryKey: ['bookings', params],
    queryFn: () => bookingApi.getBookings(params),
    staleTime: 2 * 60 * 1000, // Consider data stale after 2 minutes
    ...options
  });
};

// Alias for client bookings (same as useBookings but more semantic)
export const useClientBookings = (params = {}, options = {}) => {
  return useBookings(params, options);
};

// Get single booking
export const useBooking = (bookingId, options = {}) => {
  return useQuery({
    queryKey: ['booking', bookingId],
    queryFn: () => bookingApi.getById(bookingId),
    enabled: !!bookingId,
    staleTime: 2 * 60 * 1000,
    ...options
  });
};

// Create booking mutation
export const useCreateBooking = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: bookingApi.create,
    onSuccess: (data) => {
      // Invalidate and refetch bookings
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      
      // Add the new booking to the cache
      queryClient.setQueryData(['booking', data.data.id], data);
      
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

// Update booking mutation
export const useUpdateBooking = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: bookingApi.update,
    onSuccess: (data, variables) => {
      // Update the specific booking in cache
      queryClient.setQueryData(['booking', variables.id], data);
      
      // Invalidate bookings list
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

// Cancel booking mutation
export const useCancelBooking = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ bookingId, cancellationReason }) => 
      bookingApi.cancel(bookingId, cancellationReason),
    onSuccess: (data, variables) => {
      // Update the specific booking in cache
      queryClient.setQueryData(['booking', variables.bookingId], data);
      
      // Invalidate bookings list
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

// Update booking status mutation
export const useUpdateBookingStatus = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: bookingApi.updateStatus,
    onSuccess: (data, variables) => {
      // Update the specific booking in cache
      queryClient.setQueryData(['booking', variables.bookingId], data);
      
      // Invalidate bookings list
      queryClient.invalidateQueries({ queryKey: ['bookings'] });
      
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

// Get booking timeline
export const useBookingTimeline = (bookingId, options = {}) => {
  return useQuery({
    queryKey: ['booking', bookingId, 'timeline'],
    queryFn: () => bookingApi.getTimeline(bookingId),
    enabled: !!bookingId,
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

// Add booking notes mutation
export const useAddBookingNotes = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: bookingApi.addNotes,
    onSuccess: (data, variables) => {
      // Invalidate booking timeline
      queryClient.invalidateQueries({ 
        queryKey: ['booking', variables.bookingId, 'timeline'] 
      });
      
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

// Get booking reviews
export const useBookingReviews = (bookingId, options = {}) => {
  return useQuery({
    queryKey: ['booking', bookingId, 'reviews'],
    queryFn: () => bookingApi.getReviews(bookingId),
    enabled: !!bookingId,
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

// Submit booking review mutation
export const useSubmitBookingReview = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: bookingApi.submitReview,
    onSuccess: (data, variables) => {
      // Invalidate booking reviews
      queryClient.invalidateQueries({ 
        queryKey: ['booking', variables.bookingId, 'reviews'] 
      });
      
      // Invalidate the booking itself to update review status
      queryClient.invalidateQueries({ 
        queryKey: ['booking', variables.bookingId] 
      });
      
      if (options.onSuccess) {
        options.onSuccess(data);
      }
    },
    onError: (error) => {
      if (options.onError) {
        options.onError(error);
      }
    },
    ...options
  });
};

export default bookingApi;
